const SOURCE_KEY = "fishystuff.client.v1";
export const LEGACY_CLIENT_V1_IMPORT_VERSION = 1;

const MAX_LEGACY_BYTES = 16_384;
const MAX_TEXT_LENGTH = 256;
const MAX_AUTHOR_ID_LENGTH = 128;
const MAX_DISPLAY_NAME_LENGTH = 80;
const MAX_HANDLE_LENGTH = 32;
const MAX_REVISION_LENGTH = 128;
const HANDLE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const LEGACY_ROOT_FIELDS = Object.freeze([
  "actor",
  "localProfile",
  "preferences",
  "version",
]);
const LEGACY_ACTOR_FIELDS = Object.freeze([
  "accountId",
  "displayName",
  "kind",
  "provider",
]);
const LEGACY_PROFILE_FIELDS = Object.freeze([
  "createdAt",
  "displayName",
  "handle",
  "id",
]);
const LEGACY_PREFERENCES_FIELDS = Object.freeze(["telemetry"]);
const LEGACY_TELEMETRY_FIELDS = Object.freeze(["continuous", "diagnosticReports"]);
const LEGACY_CONTINUOUS_FIELDS = Object.freeze(["choice", "updatedAt"]);
const LEGACY_DIAGNOSTIC_FIELDS = Object.freeze(["lastPreparedAt", "lastSubmittedAt"]);
const AUTHOR_FIELDS = Object.freeze(["authorId", "displayName", "handle"]);
const AUTHOR_STAGE_FIELDS = Object.freeze([
  "author",
  "documentVersion",
  "sourceKey",
  "stagedAtUtc",
]);
const RECEIPT_FIELDS = Object.freeze([
  "author",
  "documentVersion",
  "recordedAtUtc",
  "result",
  "sourceKey",
]);
const QUARANTINE_FIELDS = Object.freeze([
  "documentVersion",
  "quarantinedAtUtc",
  "raw",
  "sourceKey",
  "sourceState",
]);

function isRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function exactFields(value, fields) {
  if (!isRecord(value)) return false;
  const actual = Object.keys(value).sort();
  return actual.length === fields.length
    && actual.every((field, index) => field === fields[index]);
}

function unicodeLength(value) {
  return Array.from(value).length;
}

function canonicalBoundedString(
  value,
  { allowEmpty = true, max = MAX_TEXT_LENGTH, unicode = false } = {},
) {
  return typeof value === "string"
    && value.length <= (unicode ? Number.POSITIVE_INFINITY : max)
    && (!unicode || unicodeLength(value) <= max)
    && value.trim() === value
    && (allowEmpty || value.length > 0);
}

export function isCanonicalLegacyClientUtcOrEmpty(value) {
  if (value === "") return true;
  if (typeof value !== "string" || value.length < 20 || value.length > 32) return false;
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

export function isValidLegacyClientImportRevision(value) {
  return canonicalBoundedString(value, {
    allowEmpty: false,
    max: MAX_REVISION_LENGTH,
  });
}

function invalid(reason) {
  return { kind: "invalid", reason };
}

function unsupportedVersion(foundVersion) {
  return { kind: "unsupportedVersion", foundVersion };
}

function decodeLegacyAuthor(value) {
  if (!exactFields(value, AUTHOR_FIELDS)) {
    return invalid("legacy-author-fields-must-match-v1");
  }
  if (!canonicalBoundedString(value.authorId, {
    allowEmpty: false,
    max: MAX_AUTHOR_ID_LENGTH,
  })) {
    return invalid("legacy-author-id-must-be-canonical");
  }
  if (!canonicalBoundedString(value.displayName, {
    max: MAX_DISPLAY_NAME_LENGTH,
    unicode: true,
  })) {
    return invalid("legacy-author-display-name-must-be-canonical");
  }
  if (
    !canonicalBoundedString(value.handle, { max: MAX_HANDLE_LENGTH })
    || (value.handle !== "" && !HANDLE_PATTERN.test(value.handle))
  ) {
    return invalid("legacy-author-handle-must-be-canonical");
  }
  return {
    kind: "ready",
    author: Object.freeze({
      authorId: value.authorId,
      displayName: value.displayName,
      handle: value.handle,
    }),
  };
}

function decodeSourceState(value) {
  if (!isRecord(value) || typeof value.kind !== "string") {
    return invalid("quarantine-source-state-must-be-an-object");
  }
  if (value.kind === "invalid") {
    if (
      !exactFields(value, ["kind", "reason"])
      || !canonicalBoundedString(value.reason, { allowEmpty: false })
    ) {
      return invalid("invalid-source-state-fields-must-match-v1");
    }
    return { kind: "ready", sourceState: Object.freeze({ ...value }) };
  }
  if (value.kind === "unsupportedVersion") {
    if (
      !exactFields(value, ["foundVersion", "kind"])
      || !Number.isSafeInteger(value.foundVersion)
      || value.foundVersion <= LEGACY_CLIENT_V1_IMPORT_VERSION
    ) {
      return invalid("unsupported-source-state-fields-must-match-v1");
    }
    return { kind: "ready", sourceState: Object.freeze({ ...value }) };
  }
  if (value.kind === "requiresReconsent") {
    if (
      !exactFields(value, ["kind", "reason"])
      || value.reason !== "legacy-enabled-has-no-versioned-consent"
    ) {
      return invalid("reconsent-source-state-fields-must-match-v1");
    }
    return { kind: "ready", sourceState: Object.freeze({ ...value }) };
  }
  return invalid("quarantine-source-state-kind-is-unsupported");
}

export function decodeLegacyClientV1(raw) {
  if (typeof raw !== "string") return invalid("stored-value-must-be-a-string");
  if (raw.length > MAX_LEGACY_BYTES) return invalid("stored-value-exceeds-v1-limit");
  let document;
  try {
    document = JSON.parse(raw);
  } catch (_error) {
    return invalid("malformed-json");
  }
  if (!isRecord(document)) return invalid("document-must-be-an-object");
  if (
    Number.isSafeInteger(document.version)
    && document.version > LEGACY_CLIENT_V1_IMPORT_VERSION
  ) {
    return unsupportedVersion(document.version);
  }
  if (!exactFields(document, LEGACY_ROOT_FIELDS)) return invalid("root-fields-must-match-v1");
  if (document.version !== LEGACY_CLIENT_V1_IMPORT_VERSION) return invalid("version-must-be-1");
  if (!exactFields(document.actor, LEGACY_ACTOR_FIELDS)) {
    return invalid("actor-fields-must-match-v1");
  }
  if (
    (document.actor.kind !== "guest" && document.actor.kind !== "user")
    || !canonicalBoundedString(document.actor.provider)
    || !canonicalBoundedString(document.actor.accountId)
    || !canonicalBoundedString(document.actor.displayName, { allowEmpty: false })
  ) {
    return invalid("actor-values-must-be-canonical-v1");
  }
  if (!exactFields(document.localProfile, LEGACY_PROFILE_FIELDS)) {
    return invalid("local-profile-fields-must-match-v1");
  }
  const author = decodeLegacyAuthor({
    authorId: document.localProfile.id,
    displayName: document.localProfile.displayName,
    handle: document.localProfile.handle,
  });
  if (author.kind !== "ready") return author;
  if (
    !isCanonicalLegacyClientUtcOrEmpty(document.localProfile.createdAt)
    || document.localProfile.createdAt === ""
  ) {
    return invalid("local-profile-created-at-must-match-v1");
  }
  if (!exactFields(document.preferences, LEGACY_PREFERENCES_FIELDS)) {
    return invalid("preferences-fields-must-match-v1");
  }
  const telemetry = document.preferences.telemetry;
  if (!exactFields(telemetry, LEGACY_TELEMETRY_FIELDS)) {
    return invalid("telemetry-fields-must-match-v1");
  }
  if (!exactFields(telemetry.continuous, LEGACY_CONTINUOUS_FIELDS)) {
    return invalid("continuous-telemetry-fields-must-match-v1");
  }
  const choice = telemetry.continuous.choice;
  if (choice !== "enabled" && choice !== "disabled" && choice !== "unset") {
    return invalid("continuous-telemetry-choice-must-match-v1");
  }
  if (
    !isCanonicalLegacyClientUtcOrEmpty(telemetry.continuous.updatedAt)
    || (choice === "unset" && telemetry.continuous.updatedAt !== "")
    || (choice !== "unset" && telemetry.continuous.updatedAt === "")
  ) {
    return invalid("continuous-telemetry-time-must-match-v1");
  }
  if (!exactFields(telemetry.diagnosticReports, LEGACY_DIAGNOSTIC_FIELDS)) {
    return invalid("diagnostic-report-fields-must-match-v1");
  }
  if (
    !isCanonicalLegacyClientUtcOrEmpty(telemetry.diagnosticReports.lastPreparedAt)
    || !isCanonicalLegacyClientUtcOrEmpty(telemetry.diagnosticReports.lastSubmittedAt)
  ) {
    return invalid("diagnostic-report-times-must-match-v1");
  }
  return {
    kind: "ready",
    author: author.author,
    telemetry: Object.freeze({
      choice,
      decidedAtUtc: telemetry.continuous.updatedAt,
    }),
  };
}

export function decodeLegacyClientAuthorStageV1(raw) {
  if (typeof raw !== "string") return invalid("author-stage-must-be-a-string");
  let stage;
  try {
    stage = JSON.parse(raw);
  } catch (_error) {
    return invalid("author-stage-malformed-json");
  }
  if (!isRecord(stage)) return invalid("author-stage-must-be-an-object");
  if (
    Number.isSafeInteger(stage.documentVersion)
    && stage.documentVersion > LEGACY_CLIENT_V1_IMPORT_VERSION
  ) {
    return unsupportedVersion(stage.documentVersion);
  }
  if (!exactFields(stage, AUTHOR_STAGE_FIELDS)) {
    return invalid("author-stage-fields-must-match-v1");
  }
  if (
    stage.documentVersion !== LEGACY_CLIENT_V1_IMPORT_VERSION
    || stage.sourceKey !== SOURCE_KEY
    || !isCanonicalLegacyClientUtcOrEmpty(stage.stagedAtUtc)
    || stage.stagedAtUtc === ""
  ) {
    return invalid("author-stage-values-must-match-v1");
  }
  const author = decodeLegacyAuthor(stage.author);
  if (author.kind !== "ready") return author;
  return { kind: "ready", stage: Object.freeze({ ...stage, author: author.author }) };
}

function decodeReceiptAuthor(value) {
  if (!isRecord(value) || typeof value.kind !== "string") {
    return invalid("receipt-author-must-be-an-object");
  }
  if (value.kind === "staged" || value.kind === "discarded" || value.kind === "absent") {
    if (!exactFields(value, ["kind"])) {
      return invalid("receipt-author-fields-must-match-v1");
    }
    return { kind: "ready", author: Object.freeze({ kind: value.kind }) };
  }
  if (value.kind === "imported") {
    if (
      !exactFields(value, ["kind", "revision"])
      || !isValidLegacyClientImportRevision(value.revision)
    ) {
      return invalid("receipt-imported-author-fields-must-match-v1");
    }
    return { kind: "ready", author: Object.freeze({ ...value }) };
  }
  return invalid("receipt-author-kind-is-unsupported");
}

function decodeReceiptResult(value) {
  if (!isRecord(value) || typeof value.kind !== "string") {
    return invalid("receipt-result-must-be-an-object");
  }
  if (value.kind === "imported") {
    if (
      !exactFields(value, ["consentRevision", "kind"])
      || (
        value.consentRevision !== null
        && !isValidLegacyClientImportRevision(value.consentRevision)
      )
    ) {
      return invalid("receipt-imported-result-fields-must-match-v1");
    }
    return { kind: "ready", result: Object.freeze({ ...value }) };
  }
  if (value.kind === "requiresReconsent") {
    if (!exactFields(value, ["kind"])) {
      return invalid("receipt-reconsent-result-fields-must-match-v1");
    }
    return { kind: "ready", result: Object.freeze({ kind: value.kind }) };
  }
  if (value.kind === "quarantined") {
    if (!exactFields(value, ["kind", "sourceState"])) {
      return invalid("receipt-quarantined-result-fields-must-match-v1");
    }
    const sourceState = decodeSourceState(value.sourceState);
    if (sourceState.kind !== "ready") return sourceState;
    if (sourceState.sourceState.kind === "requiresReconsent") {
      return invalid("quarantined-result-cannot-use-reconsent-state");
    }
    return {
      kind: "ready",
      result: Object.freeze({ kind: value.kind, sourceState: sourceState.sourceState }),
    };
  }
  return invalid("receipt-result-kind-is-unsupported");
}

export function decodeLegacyClientImportReceiptV1(raw) {
  if (typeof raw !== "string") return invalid("receipt-must-be-a-string");
  let receipt;
  try {
    receipt = JSON.parse(raw);
  } catch (_error) {
    return invalid("receipt-malformed-json");
  }
  if (!isRecord(receipt)) return invalid("receipt-must-be-an-object");
  if (
    Number.isSafeInteger(receipt.documentVersion)
    && receipt.documentVersion > LEGACY_CLIENT_V1_IMPORT_VERSION
  ) {
    return unsupportedVersion(receipt.documentVersion);
  }
  if (!exactFields(receipt, RECEIPT_FIELDS)) return invalid("receipt-fields-must-match-v1");
  if (
    receipt.documentVersion !== LEGACY_CLIENT_V1_IMPORT_VERSION
    || receipt.sourceKey !== SOURCE_KEY
    || !isCanonicalLegacyClientUtcOrEmpty(receipt.recordedAtUtc)
    || receipt.recordedAtUtc === ""
  ) {
    return invalid("receipt-values-must-match-v1");
  }
  const author = decodeReceiptAuthor(receipt.author);
  if (author.kind !== "ready") return author;
  const result = decodeReceiptResult(receipt.result);
  if (result.kind !== "ready") return result;
  if (result.result.kind === "quarantined" && author.author.kind !== "absent") {
    return invalid("quarantined-receipt-must-have-no-author-stage");
  }
  if (result.result.kind !== "quarantined" && author.author.kind === "absent") {
    return invalid("valid-source-receipt-must-track-an-author-phase");
  }
  return {
    kind: "ready",
    receipt: Object.freeze({
      ...receipt,
      author: author.author,
      result: result.result,
    }),
  };
}

export function decodeLegacyClientQuarantineV1(raw) {
  if (typeof raw !== "string") return invalid("quarantine-must-be-a-string");
  let quarantine;
  try {
    quarantine = JSON.parse(raw);
  } catch (_error) {
    return invalid("quarantine-malformed-json");
  }
  if (!isRecord(quarantine)) return invalid("quarantine-must-be-an-object");
  if (
    Number.isSafeInteger(quarantine.documentVersion)
    && quarantine.documentVersion > LEGACY_CLIENT_V1_IMPORT_VERSION
  ) {
    return unsupportedVersion(quarantine.documentVersion);
  }
  if (!exactFields(quarantine, QUARANTINE_FIELDS)) {
    return invalid("quarantine-fields-must-match-v1");
  }
  if (
    quarantine.documentVersion !== LEGACY_CLIENT_V1_IMPORT_VERSION
    || quarantine.sourceKey !== SOURCE_KEY
    || !isCanonicalLegacyClientUtcOrEmpty(quarantine.quarantinedAtUtc)
    || quarantine.quarantinedAtUtc === ""
    || typeof quarantine.raw !== "string"
    || quarantine.raw.length > MAX_LEGACY_BYTES
  ) {
    return invalid("quarantine-values-must-match-v1");
  }
  const sourceState = decodeSourceState(quarantine.sourceState);
  if (sourceState.kind !== "ready") return sourceState;
  return {
    kind: "ready",
    quarantine: Object.freeze({ ...quarantine, sourceState: sourceState.sourceState }),
  };
}

export function createLegacyClientAuthorStageV1(author, stagedAtUtc) {
  const decoded = decodeLegacyClientAuthorStageV1(JSON.stringify({
    documentVersion: LEGACY_CLIENT_V1_IMPORT_VERSION,
    sourceKey: SOURCE_KEY,
    stagedAtUtc,
    author,
  }));
  if (decoded.kind !== "ready") {
    throw new TypeError(`Legacy author stage is invalid: ${decoded.reason ?? decoded.kind}.`);
  }
  return decoded.stage;
}

export function createLegacyClientImportReceiptV1(author, result, recordedAtUtc) {
  const decoded = decodeLegacyClientImportReceiptV1(JSON.stringify({
    documentVersion: LEGACY_CLIENT_V1_IMPORT_VERSION,
    sourceKey: SOURCE_KEY,
    recordedAtUtc,
    author,
    result,
  }));
  if (decoded.kind !== "ready") {
    throw new TypeError(`Legacy import receipt is invalid: ${decoded.reason ?? decoded.kind}.`);
  }
  return decoded.receipt;
}

export function createLegacyClientQuarantineV1(raw, sourceState, quarantinedAtUtc) {
  const decoded = decodeLegacyClientQuarantineV1(JSON.stringify({
    documentVersion: LEGACY_CLIENT_V1_IMPORT_VERSION,
    sourceKey: SOURCE_KEY,
    quarantinedAtUtc,
    raw,
    sourceState,
  }));
  if (decoded.kind !== "ready") {
    throw new TypeError(`Legacy quarantine is invalid: ${decoded.reason ?? decoded.kind}.`);
  }
  return decoded.quarantine;
}

export function legacyClientAuthorsEqual(left, right) {
  return left.authorId === right.authorId
    && left.displayName === right.displayName
    && left.handle === right.handle;
}

export function legacyClientImportReceiptProjection(receipt) {
  if (receipt.result.kind === "quarantined") {
    return Object.freeze({
      kind: "quarantined",
      sourceState: receipt.result.sourceState,
    });
  }
  return Object.freeze({
    kind: "complete",
    outcome: receipt.result.kind,
    authorPhase: receipt.author.kind,
    authorRevision: receipt.author.kind === "imported" ? receipt.author.revision : null,
    consentRevision: receipt.result.kind === "imported"
      ? receipt.result.consentRevision
      : null,
  });
}
